import { Result, failure, success } from '../shared'
import { CollateralProvider } from './collateral.provider'
import {
  CollateralSearchParams,
  CollateralServiceErrorCode,
  CollateralFailure,
  HypothecCollateralsRequestServerData,
  CollateralViewParams,
  CollateralAddParams,
  CollateralAddPermissionParams,
  CollateralOperActions,
  CollateralDeletePermissionParams,
  CollateralOperationsParams,
  CollateralVisaParams,
  CollateralUndoVisaParams,
  CollateralUpdateAgreementNumberParams,
  CollateralEditStateParams,
  CollateralEditDetailStateParams,
  CollateralUpdateDescParams,
  CollateralManagementViewActions,
  CollateralManagementOperActions,
  CollateralManagementViewSessionsParams,
  CollateralManagementPrintSessionsParams,
  CollateralManagementViewWorklistParams,
  CollateralManagementLoadWorklistParams,
  CollateralManagementClearWorklistParams,
  CollateralManagementAddWorklistParams,
  CollateralManagementDeleteWorklistParams,
  CollateralManagementProcessWorklistParams,
} from './collateral.types'

export class CollateralService {
  constructor(private provider: CollateralProvider) {}

  public async collateralSearch(
    params: CollateralSearchParams,
    signal?: AbortSignal,
  ): Promise<Result<HypothecCollateralsRequestServerData, CollateralFailure>> {
    try {
      const { data } = await this.provider.collateralSearch(params, signal)
      return success(data)
    } catch (error) {
      return failure(CollateralServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async collateralView(
    params: CollateralViewParams,
  ): Promise<Result<HypothecCollateralsRequestServerData, CollateralFailure>> {
    try {
      const { data } = await this.provider.collateralView(params)
      return success(data)
    } catch (error) {
      return failure(CollateralServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async collateralPrint(
    params: CollateralViewParams,
  ): Promise<Result<HypothecCollateralsRequestServerData, CollateralFailure>> {
    try {
      const { data } = await this.provider.collateralView(params)
      return success(data)
    } catch (error) {
      return failure(CollateralServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async collateralStateHistory(
    params: CollateralViewParams,
  ): Promise<Result<HypothecCollateralsRequestServerData, CollateralFailure>> {
    try {
      const { data } = await this.provider.collateralView({
        ...params,
        page: 'statehistory',
      })
      return success(data)
    } catch (error) {
      return failure(CollateralServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async collateralNewEstimation(
    params: CollateralViewParams,
  ): Promise<Result<HypothecCollateralsRequestServerData, CollateralFailure>> {
    try {
      const { data } = await this.provider.collateralView({ ...params, page: 'newestimation' })
      return success(data)
    } catch (error) {
      return failure(CollateralServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async collateralAdd(
    params: CollateralAddParams,
  ): Promise<Result<HypothecCollateralsRequestServerData, CollateralFailure>> {
    try {
      const { data } = await this.provider.collateralAdd(params)
      return success(data)
    } catch (error) {
      return failure(CollateralServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async collateralAddPermission(
    params: CollateralAddPermissionParams,
  ): Promise<Result<HypothecCollateralsRequestServerData, CollateralFailure>> {
    try {
      await this.provider.collateralOper({ ...params, action: CollateralOperActions.ACCESSPERMADD })
      return success()
    } catch (error) {
      return failure(CollateralServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async collateralDeletePermission(
    params: CollateralDeletePermissionParams,
  ): Promise<Result<HypothecCollateralsRequestServerData, CollateralFailure>> {
    try {
      await this.provider.collateralOper({ ...params, action: CollateralOperActions.ACCESSPERMDELETE })
      return success()
    } catch (error) {
      return failure(CollateralServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async collateralOperations(
    params: CollateralOperationsParams,
  ): Promise<Result<HypothecCollateralsRequestServerData, CollateralFailure>> {
    try {
      await this.provider.collateralOper({ ...params })
      return success()
    } catch (error) {
      return failure(CollateralServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async collateralVisa(
    params: CollateralVisaParams,
  ): Promise<Result<HypothecCollateralsRequestServerData, CollateralFailure>> {
    try {
      await this.provider.collateralOper({ ...params, action: CollateralOperActions.VISA })
      return success()
    } catch (error) {
      return failure(CollateralServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async collateralUndoVisa(
    params: CollateralUndoVisaParams,
  ): Promise<Result<HypothecCollateralsRequestServerData, CollateralFailure>> {
    try {
      await this.provider.collateralOper({ ...params, action: CollateralOperActions.VISAUNDO })
      return success()
    } catch (error) {
      return failure(CollateralServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async collateralUpdateAgreementNumber(
    params: CollateralUpdateAgreementNumberParams,
  ): Promise<Result<HypothecCollateralsRequestServerData, CollateralFailure>> {
    try {
      await this.provider.collateralOper({ ...params, action: CollateralOperActions.AGREEMENTNUMBERUPDATE })
      return success()
    } catch (error) {
      return failure(CollateralServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async collateralEditState(
    params: CollateralEditStateParams,
  ): Promise<Result<HypothecCollateralsRequestServerData, CollateralFailure>> {
    try {
      await this.provider.collateralOper({ ...params, action: CollateralOperActions.STATEUPDATE })
      return success()
    } catch (error) {
      return failure(CollateralServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async collateralEditDetailState(
    params: CollateralEditDetailStateParams,
  ): Promise<Result<HypothecCollateralsRequestServerData, CollateralFailure>> {
    try {
      await this.provider.collateralOper({ ...params, action: CollateralOperActions.DETAILSSTATEUPDATE })
      return success()
    } catch (error) {
      return failure(CollateralServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async collateralUpdateDesc(
    params: CollateralUpdateDescParams,
  ): Promise<Result<HypothecCollateralsRequestServerData, CollateralFailure>> {
    try {
      await this.provider.collateralOper({ ...params, action: CollateralOperActions.UPDATEDESC })
      return success()
    } catch (error) {
      return failure(CollateralServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async collateralManagementViewSessions(
    params: CollateralManagementViewSessionsParams,
  ): Promise<Result<HypothecCollateralsRequestServerData, CollateralFailure>> {
    try {
      const { data } = await this.provider.collateralMngView({
        ...params,
        action: CollateralManagementViewActions.VIEWSESSIONS,
      })
      return success(data)
    } catch (error) {
      return failure(CollateralServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async collateralManagementPrintSessions(
    params: CollateralManagementPrintSessionsParams,
  ): Promise<Result<HypothecCollateralsRequestServerData, CollateralFailure>> {
    try {
      const { data } = await this.provider.collateralMngView({
        ...params,
        action: CollateralManagementViewActions.PRINTSESSION,
      })
      return success(data)
    } catch (error) {
      return failure(CollateralServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async collateralManagementViewWorklist(
    params: CollateralManagementViewWorklistParams,
  ): Promise<Result<HypothecCollateralsRequestServerData, CollateralFailure>> {
    try {
      const result = await this.provider.collateralMngView({
        ...params,
        action: CollateralManagementViewActions.VIEWWORKLIST,
      })
      return success(result.data)
    } catch (error) {
      return failure(CollateralServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async printCollateralManagement(
    params: CollateralManagementViewWorklistParams & { action: string },
  ): Promise<Result<HypothecCollateralsRequestServerData, CollateralFailure>> {
    try {
      const result = await this.provider.collateralMngView({
        ...params,
      })
      return success(result.data)
    } catch (error) {
      return failure(CollateralServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async collateralManagementLoadWorklist(
    params: CollateralManagementLoadWorklistParams,
  ): Promise<Result<void, CollateralFailure>> {
    try {
      await this.provider.collateralMngOper({ ...params, action: CollateralManagementOperActions.LOADWORKLIST })
      return success()
    } catch (error) {
      return failure(CollateralServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async collateralManagementClearWorklist(
    params: CollateralManagementClearWorklistParams,
  ): Promise<Result<void, CollateralFailure>> {
    try {
      await this.provider.collateralMngOper({ ...params, action: CollateralManagementOperActions.CLEARWORKLIST })
      return success()
    } catch (error) {
      return failure(CollateralServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async collateralManagementAddWorklist(
    params: CollateralManagementAddWorklistParams,
  ): Promise<Result<void, CollateralFailure>> {
    try {
      await this.provider.collateralMngOper({ ...params, action: CollateralManagementOperActions.ADDWORKLIST })
      return success()
    } catch (error) {
      return failure(CollateralServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async collateralManagementDeleteWorklist(
    params: CollateralManagementDeleteWorklistParams,
  ): Promise<Result<void, CollateralFailure>> {
    try {
      await this.provider.collateralMngOper({ ...params, action: CollateralManagementOperActions.DELETEWORKLIST })
      return success()
    } catch (error) {
      return failure(CollateralServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }

  public async collateralManagementProcessWorklist(
    params: CollateralManagementProcessWorklistParams,
  ): Promise<Result<any, CollateralFailure>> {
    try {
      const result = await this.provider.collateralMngOper({
        ...params,
        action: CollateralManagementOperActions.PROCESSWORKLIST,
      })
      return success(result.data)
    } catch (error) {
      return failure(CollateralServiceErrorCode.GENERAL, {
        message: error.Message || error.Text,
      })
    }
  }
}
